import { Button } from 'components/Button/Button';
import { TextInput } from 'components/Inputs/TextInput/TextInput';
import { useDeviceCategory } from 'components/Layout/useDeviceCategory';
import type { JSX } from 'react';
import { useIntl } from 'react-intl';
import { AZURE_PASSWORD_RESET_POLICY } from 'services/api/constants';
import { PasswordInfo } from 'services/api/myProfile/types';
import { HttpError } from 'services/api/types';
import { redirectToPasswordReset } from 'services/authentication/service';
import { useIntlByKey } from 'services/i18n/hooks/useIntlByKey';
import { formatPhoneNumberInput } from 'services/utils/formatNumber';
import { getSpacing } from 'stylesheet';

import {
  ButtonsContainer,
  ChangePasswordLink,
  FormRowContainer,
  MyProfileFormContainer,
  PasswordExpiredValue,
  PasswordInfoLabel,
  PasswordInfoRow,
  PasswordInfoValue,
  PasswordSection,
} from './MyProfileForm.style';
import {
  ProfileInformation,
  ProfileInformationFormKeys,
  useMyProfileFormik,
} from './useMyProfileFormik';

type MyProfileFormProps = {
  initialFormValues: ProfileInformation;
  onSubmit: (profileInfo: ProfileInformation) => Promise<void>;
  onSubmitError: (error: HttpError) => void;
  onAbort: () => void;
  passwordInfo?: PasswordInfo | null;
};

export const MyProfileForm = ({
  initialFormValues,
  onSubmit,
  onSubmitError,
  onAbort,
  passwordInfo,
}: MyProfileFormProps): JSX.Element => {
  const myProfileFormIntl = useIntlByKey('my-profile-form');
  const intl = useIntl();

  const formik = useMyProfileFormik(initialFormValues, onSubmit, onSubmitError);
  const { isMobileAndTablet } = useDeviceCategory();

  const lastChangedLabel = passwordInfo?.lastPasswordChangeDateTime
    ? intl.formatDate(passwordInfo.lastPasswordChangeDateTime, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : myProfileFormIntl('form.password.never');

  const renderExpirationValue = () => {
    if (!passwordInfo || passwordInfo.daysUntilExpiration === null) {
      return <PasswordInfoValue>{myProfileFormIntl('form.password.never')}</PasswordInfoValue>;
    }
    if (passwordInfo.isPasswordExpired || passwordInfo.daysUntilExpiration <= 0) {
      return (
        <PasswordExpiredValue>{myProfileFormIntl('form.password.expired')}</PasswordExpiredValue>
      );
    }
    return (
      <PasswordInfoValue>
        {myProfileFormIntl('form.password.days', { days: passwordInfo.daysUntilExpiration })}
      </PasswordInfoValue>
    );
  };
  return (
    <MyProfileFormContainer>
      <form onSubmit={formik.handleSubmit}>
        <FormRowContainer>
          <TextInput
            id={ProfileInformationFormKeys.USER_FIRST_NAME}
            name={ProfileInformationFormKeys.USER_FIRST_NAME}
            label={myProfileFormIntl('form.first-name.label')}
            placeholder={myProfileFormIntl('form.first-name.placeholder')}
            value={formik.values[ProfileInformationFormKeys.USER_FIRST_NAME]}
            onChange={formik.handleChange}
            error={
              formik.touched[ProfileInformationFormKeys.USER_FIRST_NAME] &&
              Boolean(formik.errors[ProfileInformationFormKeys.USER_FIRST_NAME])
            }
            helperText={
              formik.touched[ProfileInformationFormKeys.USER_FIRST_NAME] &&
              formik.errors[ProfileInformationFormKeys.USER_FIRST_NAME]
            }
            required
          />
        </FormRowContainer>
        <FormRowContainer>
          <TextInput
            id={ProfileInformationFormKeys.USER_LAST_NAME}
            name={ProfileInformationFormKeys.USER_LAST_NAME}
            label={myProfileFormIntl('form.last-name.label')}
            placeholder={myProfileFormIntl('form.last-name.placeholder')}
            value={formik.values[ProfileInformationFormKeys.USER_LAST_NAME]}
            onChange={formik.handleChange}
            error={
              formik.touched[ProfileInformationFormKeys.USER_LAST_NAME] &&
              Boolean(formik.errors[ProfileInformationFormKeys.USER_LAST_NAME])
            }
            helperText={
              formik.touched[ProfileInformationFormKeys.USER_LAST_NAME] &&
              formik.errors[ProfileInformationFormKeys.USER_LAST_NAME]
            }
            required
          />
        </FormRowContainer>
        <FormRowContainer>
          <TextInput
            id={ProfileInformationFormKeys.USER_PHONE_NUMBER}
            name={ProfileInformationFormKeys.USER_PHONE_NUMBER}
            label={myProfileFormIntl('form.phone-number.label')}
            placeholder={myProfileFormIntl('form.phone-number.placeholder')}
            value={formik.values.phoneNumber}
            onChange={(e) =>
              formik.setFieldValue(
                ProfileInformationFormKeys.USER_PHONE_NUMBER,
                formatPhoneNumberInput(e.target.value),
              )
            }
            error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
            helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
            required
          />
        </FormRowContainer>

        <PasswordSection>
          <PasswordInfoRow>
            <PasswordInfoLabel>
              {myProfileFormIntl('form.password.last-changed-label')}
            </PasswordInfoLabel>
            <PasswordInfoValue>{lastChangedLabel}</PasswordInfoValue>
          </PasswordInfoRow>
          <PasswordInfoRow>
            <PasswordInfoLabel>
              {myProfileFormIntl('form.password.expires-label')}
            </PasswordInfoLabel>
            {renderExpirationValue()}
          </PasswordInfoRow>
          {AZURE_PASSWORD_RESET_POLICY && (
            <ChangePasswordLink type="button" onClick={() => redirectToPasswordReset()}>
              {myProfileFormIntl('form.password.change-password')}
            </ChangePasswordLink>
          )}
        </PasswordSection>

        <ButtonsContainer>
          <Button
            displayStyle="unfilledGrey"
            onClick={onAbort}
            margins={`0 ${getSpacing(1)} 0 0`}
            disabled={formik.isSubmitting}
            bigPaddings
          >
            {myProfileFormIntl('form.cancel')}
          </Button>
          <Button
            isMobile={isMobileAndTablet}
            displayStyle="filledOrange"
            type="submit"
            isLoading={formik.isSubmitting}
            bigPaddings
          >
            {myProfileFormIntl('form.update-profile')}
          </Button>
        </ButtonsContainer>
      </form>
    </MyProfileFormContainer>
  );
};
